import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { reminderApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const TemplateList = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { hasPermission } = useAuth();

  // Fetch templates on component mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  // Function to fetch templates
  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await reminderApi.getAllTemplates();
      setTemplates(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load templates. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Function to delete a template
  const handleDeleteTemplate = async (id) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await reminderApi.deleteTemplate(id);
        // Remove the deleted template from the local state
        setTemplates(templates.filter(template => template.id !== id));
      } catch (err) {
        console.error('Error deleting template:', err);
        setError('Failed to delete template. Please try again.');
      }
    }
  };

  // Function to set a template as default
  const handleSetDefault = async (id) => {
    try {
      await reminderApi.setDefaultTemplate(id);
      // Update the local state to reflect the new default template
      setTemplates(templates.map(template => ({
        ...template,
        is_default: template.id === id
      })));
    } catch (err) {
      console.error('Error setting default template:', err);
      setError('Failed to set default template. Please try again.');
    }
  };

  return (
    <div className="template-list">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Reminder Templates</h1>
        <div className="d-flex gap-2">
          {hasPermission('reminders', 'create') && (
            <Link to="/reminder-templates/new" className="btn btn-primary">
              <i className="fas fa-plus me-1"></i> New Template
            </Link>
          )}
          <Link to="/reminders" className="btn btn-outline-secondary">
            <i className="fas fa-arrow-left me-1"></i> Back to Reminders
          </Link>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : templates.length === 0 ? (
        <div className="alert alert-info" role="alert">
          No templates found. Click "New Template" to create one.
        </div>
      ) : (
        <div className="row">
          {templates.map(template => (
            <div key={template.id} className="col-md-6 col-lg-4 mb-4">
              <div className={`card h-100 shadow-sm ${template.is_default ? 'border-primary' : ''}`}>
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">
                    {template.name}
                    {template.is_default && (
                      <span className="badge bg-primary ms-2">Default</span>
                    )}
                  </h5>
                  <div className="dropdown">
                    <button className="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" id={`dropdown-${template.id}`} data-bs-toggle="dropdown" aria-expanded="false">
                      <i className="fas fa-ellipsis-v"></i>
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end" aria-labelledby={`dropdown-${template.id}`}>
                      {hasPermission('reminders', 'update') && (
                        <>
                          <li>
                            <Link to={`/reminder-templates/${template.id}/edit`} className="dropdown-item">
                              <i className="fas fa-edit me-1"></i> Edit
                            </Link>
                          </li>
                          {!template.is_default && (
                            <li>
                              <button 
                                className="dropdown-item" 
                                onClick={() => handleSetDefault(template.id)}
                              >
                                <i className="fas fa-star me-1"></i> Set as Default
                              </button>
                            </li>
                          )}
                        </>
                      )}
                      {hasPermission('reminders', 'delete') && (
                        <li>
                          <button 
                            className="dropdown-item text-danger" 
                            onClick={() => handleDeleteTemplate(template.id)}
                          >
                            <i className="fas fa-trash me-1"></i> Delete
                          </button>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
                <div className="card-body">
                  <h6 className="card-subtitle mb-2 text-muted">Subject</h6>
                  <p className="card-text">{template.subject}</p>
                  
                  <h6 className="card-subtitle mb-2 text-muted">Preview</h6>
                  <div className="card-text template-preview">
                    <div 
                      className="border rounded p-2 bg-light" 
                      style={{ maxHeight: '150px', overflow: 'auto', fontSize: '0.8rem' }}
                    >
                      <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {template.body}
                      </pre>
                    </div>
                  </div>
                </div>
                <div className="card-footer d-flex justify-content-end">
                  {hasPermission('reminders', 'update') && (
                    <Link to={`/reminder-templates/${template.id}/edit`} className="btn btn-sm btn-outline-primary">
                      <i className="fas fa-edit me-1"></i> Edit Template
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TemplateList;
